import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import { PackagingOpsItemRow } from "../../../../../../models/ProjectManagement/ProjectPackagingSwagpacks";

interface AdjustCompletePopupProps {
  open: boolean;
  items: PackagingOpsItemRow[];
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: { note: string }) => Promise<void>;
}

const isAlreadyZero = (item: PackagingOpsItemRow) =>
  (item.onHand ?? 0) === 0 && (item.reserved ?? 0) === 0;

export default function AdjustCompletePopup({
  open,
  items,
  isSubmitting = false,
  onClose,
  onSubmit,
}: AdjustCompletePopupProps) {
  const theme = useTheme();
  const [note, setNote] = useState("");
  const [isSubmitLocked, setIsSubmitLocked] = useState(false);
  const isSubmitLockedRef = useRef(false);

  const submitInProgress = isSubmitting || isSubmitLocked;

  useEffect(() => {
    if (!open) return;
    setNote("");
    setIsSubmitLocked(false);
    isSubmitLockedRef.current = false;
  }, [open]);

  const handleSubmit = async () => {
    if (isSubmitLockedRef.current || submitInProgress) {
      return;
    }
    if (!note.trim() || items.length === 0) {
      return;
    }

    isSubmitLockedRef.current = true;
    setIsSubmitLocked(true);

    try {
      await onSubmit({ note: note.trim() });
    } catch (error) {
      console.error("Failed to submit adjust complete:", error);
    } finally {
      isSubmitLockedRef.current = false;
      setIsSubmitLocked(false);
    }
  };

  const handleClose = () => {
    if (submitInProgress) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            position: "relative",
            maxHeight: { xs: "92vh", sm: "88vh" },
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Backdrop
        open={submitInProgress}
        sx={{
          position: "absolute",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          color: theme.palette.customColors.white,
          borderRadius: "16px",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          maxHeight: { xs: "92vh", sm: "88vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            flex: "0 0 auto",
          }}
        >
          <Box minWidth={0}>
            <CustomTypography variant="heading4" fontWeight={700}>
              Adjust Complete
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Remaining on-hand and reserved stock for {items.length} SKU
              {items.length === 1 ? "" : "s"} will be set to 0
            </CustomTypography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitInProgress}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <Grid2 container spacing={2} sx={{ minHeight: 0, flex: 1 }}>
            <Grid2 size={12} sx={{ minHeight: 0, display: "flex" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <CustomTypography variant="body2" fontWeight={600}>
                  SELECTED SKUS ({items.length})
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  Already-zero SKUs stay in this list and are skipped in the
                  ledger. Missing or inactive SKUs fail the whole request.
                </CustomTypography>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1.5}
                  sx={{
                    mt: 1.5,
                    overflowY: "auto",
                    maxHeight: { xs: "32vh", sm: "36vh", md: "40vh" },
                    pr: 0.5,
                  }}
                >
                  {items.length === 0 && (
                    <CustomTypography variant="body2" color="text.secondary">
                      No SKUs selected for adjust complete.
                    </CustomTypography>
                  )}
                  {items.map((item) => {
                    const alreadyZero = isAlreadyZero(item);
                    return (
                      <Box
                        key={item.sku}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: theme.palette.customColors.white,
                          border: `1px solid ${theme.palette.customColors.greyBorder}`,
                        }}
                      >
                        <Grid2 container spacing={1.5} alignItems="center">
                          <Grid2 size={{ xs: 12, sm: 6 }}>
                            <CustomTypography variant="body2" fontWeight={600}>
                              {item.productName || item.title || "—"}
                            </CustomTypography>
                            <Box sx={{ mt: 0.25 }}>
                              <SkuCopyRow
                                sku={item.clientSku}
                                fontWeight={600}
                              />
                            </Box>
                            {item.sku !== item.clientSku && (
                              <CustomTypography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.25 }}
                              >
                                Catalog SKU: {item.sku}
                              </CustomTypography>
                            )}
                            {alreadyZero && (
                              <CustomTypography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5 }}
                              >
                                Already 0 — no ledger row
                              </CustomTypography>
                            )}
                          </Grid2>
                          <Grid2 size={{ xs: 4, sm: 2 }}>
                            <CustomTypography
                              variant="body3"
                              color="text.secondary"
                            >
                              On hand
                            </CustomTypography>
                            <CustomTypography variant="body2" fontWeight={600}>
                              {item.onHand ?? 0}
                            </CustomTypography>
                          </Grid2>
                          <Grid2 size={{ xs: 4, sm: 2 }}>
                            <CustomTypography
                              variant="body3"
                              color="text.secondary"
                            >
                              Reserved
                            </CustomTypography>
                            <CustomTypography variant="body2" fontWeight={600}>
                              {item.reserved ?? 0}
                            </CustomTypography>
                          </Grid2>
                          <Grid2 size={{ xs: 4, sm: 2 }}>
                            <CustomTypography
                              variant="body3"
                              color="text.secondary"
                            >
                              Free
                            </CustomTypography>
                            <CustomTypography variant="body2" fontWeight={600}>
                              {item.free ?? 0}
                            </CustomTypography>
                          </Grid2>
                        </Grid2>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  NOTE
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  This note is copied onto every ADJUST ledger row for SKUs that
                  move
                </CustomTypography>
                <TextField
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  fullWidth
                  placeholder="Enter adjustment note"
                  multiline
                  minRows={3}
                  disabled={submitInProgress}
                  sx={{
                    mt: 1,
                    "& .MuiInputBase-root": {
                      fontSize: "0.8rem",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.8rem",
                    },
                  }}
                />
              </Paper>
            </Grid2>

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton
                label="Cancel"
                onClick={handleClose}
                variant="grayscale"
                disabled={submitInProgress}
              />
              <AppButton
                label="Adjust Complete"
                onClick={() => {
                  if (submitInProgress) return;
                  void handleSubmit();
                }}
                variant="primary"
                disabled={
                  submitInProgress || items.length === 0 || !note.trim()
                }
                customStyles={{
                  pointerEvents: submitInProgress ? "none" : "auto",
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
